/**
 * The server's clock, as seen from here (Round 3: synchronized replays).
 *
 * Every room poll carries the server's clock when the request arrived and when
 * the response left. With our own send and receive times that is one NTP-style
 * sample:
 *
 *   offset = ((serverReceived - sentAt) + (serverSent - receivedAt)) / 2
 *   delay  = (receivedAt - sentAt) - (serverSent - serverReceived)
 *
 * The server's own processing time drops out. What's left of the error is the
 * difference between the trip there and the trip back, at most delay / 2, so the
 * estimate uses the recent sample with the smallest delay.
 *
 * No imports, on purpose: the end-to-end script runs this exact file under Node
 * to measure how closely clients start a replay.
 */

export interface ClockSample {
  /** Local clock when the request left. */
  sentAt: number;
  /** Server epoch ms when the request arrived. */
  serverReceived: number;
  /** Server epoch ms when the response left. */
  serverSent: number;
  /** Local clock when the response was in hand. */
  receivedAt: number;
}

interface Estimate {
  offset: number;
  delay: number;
}

/**
 * Samples kept: about the last thirty seconds while a reveal polls every second.
 * More samples means better odds of one quick, even round trip, which is what the
 * estimate rests on; device clocks drift by microseconds over that long.
 */
export const CLOCK_SAMPLES = 30;

export class ClockSync {
  private estimates: Estimate[] = [];
  /** Local clock in epoch-like ms. Monotonic in the browser (see serverClock). */
  readonly localNow: () => number;
  private readonly keep: number;

  // Plain fields rather than parameter properties: Node's type stripping (which
  // the end-to-end script relies on) cannot handle those.
  constructor(localNow: () => number = () => Date.now(), keep = CLOCK_SAMPLES) {
    this.localNow = localNow;
    this.keep = keep;
  }

  add(sample: ClockSample): void {
    const { sentAt, serverReceived, serverSent, receivedAt } = sample;
    if (![sentAt, serverReceived, serverSent, receivedAt].every(Number.isFinite)) return;
    if (receivedAt < sentAt) return;
    const processing = Math.max(0, serverSent - serverReceived);
    const delay = Math.max(0, receivedAt - sentAt - processing);
    const offset = (serverReceived - sentAt + (serverSent - receivedAt)) / 2;
    this.estimates.push({ offset, delay });
    if (this.estimates.length > this.keep) this.estimates.shift();
  }

  get ready(): boolean {
    return this.estimates.length > 0;
  }

  private best(): Estimate | null {
    let best: Estimate | null = null;
    for (const e of this.estimates) if (!best || e.delay < best.delay) best = e;
    return best;
  }

  /** Server clock minus local clock, in ms. Zero until the first sample. */
  get offset(): number {
    return this.best()?.offset ?? 0;
  }

  /** The most the offset can be off by: half the best sample's network delay. */
  get uncertainty(): number {
    const best = this.best();
    return best ? best.delay / 2 : Infinity;
  }

  serverNow(): number {
    return this.localNow() + this.offset;
  }

  /** A server-clock instant on the local clock. */
  toLocal(serverMs: number): number {
    return serverMs - this.offset;
  }
}

/**
 * The app's one estimate of the server clock. The local clock is the monotonic
 * performance clock, so the system clock jumping mid-reveal changes nothing, and
 * toLocal() converts straight to the timebase requestAnimationFrame uses (minus
 * performance.timeOrigin).
 */
export const serverClock = new ClockSync(() =>
  typeof performance !== 'undefined' ? performance.timeOrigin + performance.now() : Date.now(),
);

/** A server-clock instant on the performance.now() / requestAnimationFrame timebase. */
export function serverToPerf(serverMs: number): number {
  return serverClock.toLocal(serverMs) - performance.timeOrigin;
}
